import { type FC, useCallback, useMemo, useState } from "react";
import { generatePath, useNavigate, useParams } from "react-router-dom";

import { useQuery } from "@apollo/client";
import { AutoSizer, VirtualTable, type VirtualTableColumn } from "@snapchat/snap-design-system";
import { type GraphQLError } from "graphql";

import { useModal } from "../../camkit/core/hooks";
import { AbsoluteRoute, type RouteParams } from "../../core/routes";
import { HostedLensStatus } from "../../gql-generated/types";
import { PageContentSpinner } from "../../layout/components";
import { useTableFilterAndSort } from "../../shared/hooks";
import { getHostedLensList } from "../api/query/getHostedLensList";
import {
    type GetHostedLensListQuery,
    type GetHostedLensListQueryVariables,
} from "../api/query/getHostedLensList.gql-generated";
import { useDisclaimerAndAcceptance } from "../hooks/useDisclaimerAndAcceptance";
import { type HostedLensListItem, useHostedLens } from "../hooks/useHostedLens";

import { EmptyHostedLens } from "./EmptyHostedLens";
import { HostedLensDisclaimerModal } from "./HostedLensDisclaimerModal";
import { getColumns } from "./HostedLensTableColumns";
import { HostedLensesCard } from "./HostedLensesList.styles";
import { rowCSS } from "./LensFolderList.styles";
import { HostedLensDisclaimerEnum, type HostedLensDisclaimerOptions } from "./disclaimerOptions";

export const HostedLensesList: FC<{ generateCTA: React.ReactNode }> = ({ generateCTA }) => {
    const navigate = useNavigate();
    const { isUpdating, updateHostedLensStatus, deleteHostedLensLink } = useHostedLens();
    const [showDisclaimerProps, showDisclaimerModal, option] = useModal<HostedLensDisclaimerOptions>();
    const { isEnabled, selectedHostedLensId, setSelectedHostedLensId, handleErrors, handleToggle } =
        useDisclaimerAndAcceptance({ option, showDisclaimerModal });
    const [searchText, setSearchText] = useState("");
    const { orgId } = useParams<RouteParams[AbsoluteRoute.ORG]>();

    const { data: hostedLensList, loading } = useQuery<GetHostedLensListQuery, GetHostedLensListQueryVariables>(
        getHostedLensList,
        {
            variables: {
                organizationId: orgId ?? "",
            },
            fetchPolicy: "network-only",
            errorPolicy: "all",
        },
    );

    const getSearchKey = useCallback(
        (lens: HostedLensListItem) => `${lens.id} ${lens.hostedLensMetadata?.name} ${lens.status} ${lens.link}`,
        [],
    );

    const { data, onColumnSort } = useTableFilterAndSort({
        tableData: hostedLensList?.getHostedLenses.lensesList ?? [],
        initialColumn: "name",
        searchText,
        getSearchKey,
    });

    const onSearch = useCallback(
        (text: string) => {
            setSearchText(text);
        },
        [setSearchText],
    );

    const onDelete = useCallback(
        (id: string) => {
            setSelectedHostedLensId(id);
            showDisclaimerModal(HostedLensDisclaimerEnum.DELETE);
        },
        [setSelectedHostedLensId, showDisclaimerModal],
    );

    const columns = useMemo(
        (): VirtualTableColumn<HostedLensListItem>[] => getColumns({ onDelete, handleToggle }),
        [onDelete, handleToggle],
    );

    const loadMoreData = useCallback((): Promise<unknown> => new Promise(() => null), []);

    const onModalAcceptance = useCallback(
        async (isAccepted: boolean) => {
            if (!isAccepted || !selectedHostedLensId) return;

            const commonProps = {
                variables: { lensID: selectedHostedLensId, organizationId: orgId ?? "" },
                refetchQueries: [getHostedLensList],
            };

            try {
                const promise =
                    option === HostedLensDisclaimerEnum.DELETE
                        ? await deleteHostedLensLink(commonProps)
                        : await updateHostedLensStatus({
                              ...commonProps,
                              variables: {
                                  ...commonProps.variables,
                                  status: isEnabled
                                      ? HostedLensStatus.HostedLensStatusPublished
                                      : HostedLensStatus.HostedLensStatusDraft,
                              },
                          });
                if (promise.errors) {
                    handleErrors(promise.errors);
                }
            } catch (error) {
                handleErrors([error as GraphQLError]);
            }
        },
        [deleteHostedLensLink, handleErrors, isEnabled, option, orgId, selectedHostedLensId, updateHostedLensStatus],
    );

    const onRowClick = useCallback(
        (_: React.MouseEvent<HTMLDivElement>, rowIndex: number) => {
            if (data[rowIndex] !== undefined) {
                if (data[rowIndex]?.hostedLensMetadata === null) {
                    return;
                }
                navigate(
                    generatePath(AbsoluteRoute.LENS, { lensId: data[rowIndex]?.hostedLensMetadata?.lensId ?? "" }),
                );
            }
        },
        [data, navigate],
    );

    if (loading) {
        return <PageContentSpinner />;
    }

    if (hostedLensList?.getHostedLenses.lensesList.length === 0) {
        return <EmptyHostedLens generateCTA={generateCTA} />;
    }

    return (
        <div>
            <HostedLensesCard searchPlaceholder="Search for a hosted lens" padBody={false} onSearch={onSearch}>
                <AutoSizer>
                    {({ height, width }) => (
                        <VirtualTable
                            isLoading={loading}
                            data={[...data]}
                            columns={columns}
                            width={width}
                            height={height}
                            hasMore={false}
                            rowHeight={60}
                            emptyMessage={"No hosted lenses found"}
                            loadMore={loadMoreData}
                            onColumnSort={onColumnSort}
                            rowClassName={rowCSS}
                            onRowClick={onRowClick}
                        />
                    )}
                </AutoSizer>
                <HostedLensDisclaimerModal
                    {...showDisclaimerProps}
                    loading={isUpdating}
                    option={option}
                    onAction={onModalAcceptance}
                />
            </HostedLensesCard>
        </div>
    );
};
